import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable follow-up reminder dialog. Today it's wired to proposals, but
 * the props are generic so it can be reused on contacts, opportunities,
 * jobs, etc., later. Creates a row in the personal reminders collection
 * via the existing POST /api/reminders endpoint — no new backend.
 */
export default function ProposalFollowUpDialog({
  open,
  onOpenChange,
  proposal,         // { id, proposal_number, client_name, total, job_id?, job_number? }
  onCreated,
}) {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  // Date is always the source of truth. Quick-pick chips just write into it.
  const [date, setDate] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [time, setTime] = useState('09:00');

  const reset = () => {
    setDate(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
    setTime('09:00');
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSave = async () => {
    if (!proposal?.id) return;
    if (!date) {
      toast.error('Pick a follow-up date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: `Follow up: ${proposal.client_name || 'Proposal'} — ${proposal.proposal_number || ''}`.trim(),
        description: `Check in on proposal ${proposal.proposal_number} ($${Number(proposal.total || 0).toFixed(2)}). Open: /proposals/${proposal.id}/preview`,
        date,
        time,
        category: 'Job',
        priority: 'Medium',
        job_id: proposal.job_id || null,
        job_number: proposal.job_number || null,
      };
      await axios.post(`${API}/api/reminders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Follow-up reminder created');
      onCreated?.();
      handleClose(false);
    } catch (err) {
      console.error('Create reminder failed', err);
      toast.error(err.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setSaving(false);
    }
  };

  const todayPlus = (days) => format(addDays(new Date(), days), 'yyyy-MM-dd');
  const quickPicks = [
    { days: 1,  label: 'Tomorrow' },
    { days: 3,  label: '3 days' },
    { days: 7,  label: '1 week' },
    { days: 14, label: '2 weeks' },
    { days: 30, label: '1 month' },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="proposal-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-500" /> Schedule Follow-up
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {proposal && (
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <div className="font-mono">{proposal.proposal_number}</div>
              <div className="font-semibold text-slate-900">{proposal.client_name}</div>
              {proposal.total != null && (
                <div className="text-slate-500">${Number(proposal.total).toFixed(2)}</div>
              )}
            </div>
          )}

          {/* Quick chips — write directly into the date field. The chip
              for the currently-selected date gets the highlighted style. */}
          <div className="space-y-2">
            <Label>Quick pick</Label>
            <div className="flex flex-wrap gap-2">
              {quickPicks.map((q) => {
                const targetDate = todayPlus(q.days);
                const isActive = date === targetDate;
                return (
                  <Button
                    key={q.days}
                    type="button"
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    className={isActive ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' : ''}
                    onClick={() => setDate(targetDate)}
                    data-testid={`followup-preset-${q.days}`}
                  >
                    {q.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Date + time — always editable. Pick any future date, any time. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                data-testid="followup-custom-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="followup-time"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900"
            data-testid="followup-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Create Reminder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
